import React, { createContext, useState, ReactNode, useEffect } from 'react';

import { Application, EApplicationStatus, IApplication } from '../data_models/application';
import { EInvitationStatus, IInvitation, Invitation } from '../data_models/invitation';
import { ETaskStatus, ITask } from '../data_models/task';
import useApplications from '../hooks/useApplications';
import useInvitations from '../hooks/useInvitations';
import useTasks from '../hooks/useTasks';
import { MyTasksContextType } from './types/myTasksContextType';
import useCurrentUser from '../hooks/useCurrentUser';
import { EUserType } from '../data_models/enums';
import useDisplayNotifications from '../hooks/useDisplayNotifications';
import { DisplayNotificationType } from '../models/DisplayNotification';
import { set } from 'react-datepicker/dist/date_utils';
import useMyProjects from '../hooks/useMyProjects';


// Create the context
export const MyTasksContext = createContext<MyTasksContextType | undefined>(undefined);

interface MyTasksProviderProps {
    children: ReactNode;
}

// Provider component
export const MyTasksProvider: React.FC<MyTasksProviderProps> = ({ children }) => {

    // public
    const [applications, setApplications] = useState<{ [taskId: string]: Application }>({});
    const [invitations, setInvitations] = useState<{ [taskId: string]: Invitation }>({});

    const [pendingApplicationsTasks, setPendingApplicationsTasks] = useState<ITask[]>([]);
    const [acceptedApplicationsTasks, setAcceptedApplicationsTasks] = useState<ITask[]>([]);
    const [rejectedApplicationsTasks, setRejectedApplicationsTasks] = useState<ITask[]>([]);
    const [pendingInvitationsTasks, setPendingInvitationsTasks] = useState<ITask[]>([]);

    const [tasksAsFreelancerInProgress, setTasksAsFreelancerInProgress] = useState<ITask[]>([]);
    const [tasksAsFreelancerCompleted, setTasksAsFreelancerCompleted] = useState<ITask[]>([]);
    const [availableTasksAsFreelancer, setAvailableTasksAsFreelancer] = useState<ITask[]>([]);

    const [projectTasks, setProjectTasks] = useState<{ [projectID: string]: ITask[] }>({});

    const { getProjectById } = useMyProjects();

    // pagination
    const [pendingApplicationsTasksPage, setPendingApplicationsTasksPage] = useState(1);
    const [pendingApplicationsTasksLimit, setPendingApplicationsTasksLimit] = useState(10);
    const [acceptedApplicationsTasksPage, setAcceptedApplicationsTasksPage] = useState(1);
    const [acceptedApplicationsTasksLimit, setAcceptedApplicationsTasksLimit] = useState(10);
    const [rejectedApplicationsTasksPage, setRejectedApplicationsTasksPage] = useState(1);
    const [rejectedApplicationsTasksLimit, setRejectedApplicationsTasksLimit] = useState(10);
    const [pendingInvitationsTasksPage, setPendingInvitationsTasksPage] = useState(1);
    const [pendingInvitationsTasksLimit, setPendingInvitationsTasksLimit] = useState(10);

    const [availableTasksAsFreelancerPage, setAvailableTasksAsFreelancerPage] = useState(1);
    const [availableTasksAsFreelancerLimit, setAvailableTasksAsFreelancerLimit] = useState(10);
    const [tasksAsFreelancerInProgressPage, setTasksAsFreelancerInProgressPage] = useState(1);
    const [tasksAsFreelancerInProgressLimit, setTasksAsFreelancerInProgressLimit] = useState(10);
    const [tasksAsFreelancerCompletedPage, setTasksAsFreelancerCompletedPage] = useState(1);
    const [tasksAsFreelancerCompletedLimit, setTasksAsFreelancerCompletedLimit] = useState(10);




    // private
    const [blockFetch, setBlockFetch] = useState(false);

    const { addNotification } = useDisplayNotifications();
    const { fetchApplicationsForUser,
        createApplication,
        confirmApplication: _confirmApplication,
        refuseApplication: _refuseApplication
    } = useApplications();
    const { fetchInvitationsForUser,
        acceptInvitation: _acceptInvitation,
        declineInvitation: _declineInvitation,
    } = useInvitations();

    const { fetchTasksByIds,
        fetchTaskById,
        fetchTasksByFilter,
        fetchAllAvailableTasksAsFreelancer,
        updateStatus: _updateStatus,
    } = useTasks();
    const { currentUser, currentUserType, fetchWallet } = useCurrentUser();

    const {
        createTask: _createTask,
        generateTasksAi: _generateTasksAi,
        updateTask: _updateTask,
        deleteTask: _deleteTask,
        fetchTasksByProjectId,
        addFreelancerHours: _addFreelancerHours,
        pushRank: _pushRank,
        startAndPayTask: _startAndPayTask
    } = useTasks();

    const { refetchProjectById } = useMyProjects();


    // initially fetch tasks / applications / invitations
    useEffect(() => {
        try {
            if (currentUser && currentUserType === EUserType.FREELANCER && currentUser._emailVerified) {
                const fetch = async () => {
                    setBlockFetch(true);
                    await Promise.all([
                        fetchPendingApplications().catch(console.error),
                        fetchAcceptedApplications().catch(console.error),
                        fetchRejectedApplications().catch(console.error),
                        fetchPendingInvitations().catch(console.error),

                        fetchTasksAsFreelancerInProgress().catch(console.error),
                        fetchTasksAsFreelancerCompleted().catch(console.error),
                    ]);
                    setBlockFetch(false);
                }
                if (!blockFetch) {
                    fetch();
                }

            }
        } catch (error) {
            console.error('MyTasksProvider error:', error);
        }

    }, [currentUser]);

    const fetchPendingApplications = async () => {
        if (!currentUser?._emailVerified) return;
        const filter: IApplication = { status: EApplicationStatus.PENDING };
        const taskIds: string[] = [];
        const newApplications: { [taskId: string]: Application } = { ...applications };
        await fetchApplicationsForUser(
            pendingApplicationsTasksPage,
            pendingApplicationsTasksLimit,
            filter)
            .then((applications) => {
                for (const application of applications) {
                    if (application.taskId) {
                        newApplications[application.taskId] = application;
                        taskIds.push(application.taskId);
                    }
                }
            });
        const tasks = await fetchTasksByIds(taskIds);
        setPendingApplicationsTasks(tasks);
        setApplications(newApplications);
    }

    const fetchAcceptedApplications = async () => {
        if (!currentUser?._emailVerified) return;
        const filter: IApplication = { status: EApplicationStatus.ACCEPTED };
        const taskIds: string[] = [];
        const newApplications: { [key: string]: Application } = { ...applications };
        await fetchApplicationsForUser(
            acceptedApplicationsTasksPage,
            acceptedApplicationsTasksLimit,
            filter
        ).then((applications) => {
            for (const application of applications) {
                if (application.taskId) {
                    newApplications[application.taskId] = application;
                    taskIds.push(application.taskId);
                }
            }
        });
        const tasks = await fetchTasksByIds(taskIds);
        setAcceptedApplicationsTasks(tasks);


        setApplications((prevApplications) => ({
            ...prevApplications,
            ...newApplications,
        }));
    }

    const fetchRejectedApplications = async () => {
        if (!currentUser?._emailVerified) return;
        const filter: IApplication = { status: EApplicationStatus.REJECTED };
        const taskIds: string[] = [];
        const newApplications: { [key: string]: Application } = { ...applications };
        await fetchApplicationsForUser(
            rejectedApplicationsTasksPage,
            rejectedApplicationsTasksLimit,
            filter
        ).then((applications) => {
            for (const application of applications) {
                if (application.taskId) {
                    newApplications[application.taskId] = application;
                    taskIds.push(application.taskId);
                }
            }
        });
        const tasks = await fetchTasksByIds(taskIds);
        setRejectedApplicationsTasks(tasks);
        setApplications((prevApplications) => ({
            ...prevApplications,
            ...newApplications,
        }));
    }

    const fetchPendingInvitations = async () => {
        if (!currentUser?._emailVerified) return;
        const filter: IInvitation = { status: EInvitationStatus.PENDING };
        const taskIds: string[] = [];
        const newInvitations: { [taskId: string]: Invitation } = {};
        await fetchInvitationsForUser(
            pendingInvitationsTasksPage,
            pendingInvitationsTasksLimit,
            filter)
            .then((invitations) => {
                for (const invitation of invitations) {
                    if (invitation.taskId) {
                        newInvitations[invitation.taskId] = invitation;
                        taskIds.push(invitation.taskId);
                    }
                }
            });
        const tasks = await fetchTasksByIds(taskIds);
        setInvitations((prevInvitations) => ({
            ...prevInvitations,
            ...newInvitations,
        }));

        setPendingInvitationsTasks(tasks);
    }


    const fetchProjectTasks = async (projectId: string) => {
        const tasks = await fetchTasksByProjectId(projectId);
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [projectId]: tasks,
        }));
    }

    const replaceProjectTaskLocally = (task: ITask) => {
        const projectId = task.projectId;
        if (!projectId) return;

        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [projectId!]: prevProjectTasks[projectId!]?.map((t) =>
                t._id === task._id ? task : t
            ),
        }));
    }

    const createProjectTask = async (task: ITask): Promise<ITask> => {
    

        const newTask = await _createTask(task.projectId!, task);
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [task.projectId!]: [...prevProjectTasks[task.projectId!], newTask.task],
        }));

        refetchProjectById(task.projectId!);
        return newTask.task;
    }

    const generateProjectTasksAi = async (projectId: string, props: any): Promise<ITask[]> => {
        const tasks = await _generateTasksAi(projectId, props);
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [projectId!]: tasks,
        }));

        refetchProjectById(projectId);
        return tasks;
    }

    const updateProjectTask = async (taskId: string, updateTask: ITask): Promise<ITask> => {
        const task = await _updateTask(taskId, updateTask);

        // refetch project if task updates estimatedCredits or actualCredits
        if (updateTask.estimatedCredits || updateTask.actualCredits) {
            refetchProjectById(updateTask.projectId!);
        }
        // check if task updates estimatedCredits or actualCredits
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [updateTask.projectId!]: prevProjectTasks[updateTask.projectId!]?.map((t) =>
                t._id === taskId ? task : t
            ),
        }));

        return task;
    }

    const startAndPayTask = async (taskId: string): Promise<ITask> => {
        const result = await _startAndPayTask(taskId);
        const updatedTask = result.task;

        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [updatedTask.projectId!]: prevProjectTasks[updatedTask.projectId!]?.map((t) =>
                t._id === taskId ? updatedTask : t
            ),
        }));

        fetchWallet();
        return updatedTask;
    }

    const pushRankProjectTask = async (taskId: string, rank: number) => {
        const result = await _pushRank(taskId, rank);

        const updatedTasks = result.allTasks;
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [result.pushedTask.projectId!]: updatedTasks,
        }));
    }

    const deleteProjectTask = async (taskId: string, projectId: string) => {
        await _deleteTask(taskId);
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [projectId]: prevProjectTasks[projectId]?.filter(t => t._id !== taskId),
        }));

        refetchProjectById(projectId);
    }

    const addFreelancerHours = async (taskId: string, hours: number): Promise<ITask> => {
        const task = await _addFreelancerHours(taskId, hours);
        setProjectTasks((prevProjectTasks) => ({
            ...prevProjectTasks,
            [task.projectId!]: prevProjectTasks[task.projectId!]?.map(t => task._id === t._id ? task : t),
        }));

        setTasksAsFreelancerInProgress([...tasksAsFreelancerInProgress.map(t => task._id === t._id ? task : t)]);
        return task;
    }


    const applyForTask = async (projectId: string, taskId: string, message?: string): Promise<void> => {
        try {
            const application = await createApplication(projectId!, taskId!, message) as Application;
            const newApplications = { ...applications };
            newApplications[taskId] = application;
            setApplications((prevApplications) => ({
                ...prevApplications,
                ...newApplications,
            }));

            const task = await fetchTaskById(taskId);
            setPendingApplicationsTasks([...pendingApplicationsTasks, task]);

            const newAvailableTasks = availableTasksAsFreelancer.filter((t) => t._id !== taskId);
            setAvailableTasksAsFreelancer(newAvailableTasks);

            replaceProjectTaskLocally(task);
            addNotification({
                message: "Application successfully sent!",
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }

    }

    const confirmApplication = async (applicationId: string) => {
        try {
            const result = await _confirmApplication(applicationId);
            const newApplications = { ...applications };

            newApplications[result.application.taskId!] = result.application;
            setApplications((prevApplications) => ({
                ...prevApplications,
                ...newApplications,
            }));

            const newAcceptedApplications = acceptedApplicationsTasks.filter((t) => t._id !== result.application.taskId);
            setAcceptedApplicationsTasks(newAcceptedApplications);
            setTasksAsFreelancerInProgress([...tasksAsFreelancerInProgress, result.task]);

            replaceProjectTaskLocally(result.task);
            addNotification({
                message: "Application successfully confirmed!",
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const refuseApplication = async (applicationId: string) => {
        try {
            const application = (await _refuseApplication(applicationId)).application;
            const newApplications = { ...applications };
            newApplications[application.taskId!] = application;
            setApplications(newApplications);

            const newAcceptedApplications = acceptedApplicationsTasks.filter((t) => t._id !== application.taskId);
            setAcceptedApplicationsTasks(newAcceptedApplications);

            const task = acceptedApplicationsTasks.filter((t) => t._id === application.taskId);
            if (task[0]) {
                replaceProjectTaskLocally(task[0]);
            }

            addNotification({
                message: "Application successfully refused!",
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const acceptInvitation = async (invitationId: string) => {
        try {
            const result = await _acceptInvitation(invitationId);
            const newInvitations = { ...invitations };
            newInvitations[result.invitation.taskId] = result.invitation;
            setInvitations((prevInvitations) => ({
                ...prevInvitations,
                ...newInvitations,
            }));

            const newTasksAsFreelancerInProgress = [result.task, ...tasksAsFreelancerInProgress];
            setTasksAsFreelancerInProgress(newTasksAsFreelancerInProgress);

            const newAvailableTasks = availableTasksAsFreelancer.filter((t) => t._id !== result.invitation.taskId);
            setAvailableTasksAsFreelancer(newAvailableTasks);

            const newPendingInvitations = pendingInvitationsTasks.filter((t) => t._id !== result.invitation.taskId);
            setPendingInvitationsTasks(newPendingInvitations);

            replaceProjectTaskLocally(result.task);
            addNotification({
                message: "Invitation successfully accepted!",
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const declineInvitation = async (invitationId: string) => {
        try {
            const invitation = await _declineInvitation(invitationId);
            const newInvitations = { ...invitations };
            newInvitations[invitation.taskId] = invitation;
            setInvitations((prevInvitations) => ({
                ...prevInvitations,
                ...newInvitations,
            }));

            const newPendingInvitations = pendingInvitationsTasks.filter((t) => t._id !== invitation.taskId);
            setPendingInvitationsTasks(newPendingInvitations);


            addNotification({
                message: "Invitation successfully declined!",
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const fetchTasksAsFreelancerInProgress = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !currentUser?._emailVerified) return;
        const filter = {
            currentlyAssignedTo: currentUser._id,
            currentStatus: ETaskStatus.IN_PROGRESS,
        }
        const tasks = await fetchTasksByFilter(filter,
            tasksAsFreelancerInProgressPage,
            tasksAsFreelancerInProgressLimit);
        setTasksAsFreelancerInProgress(tasks);
    }

    const fetchTasksAsFreelancerCompleted = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !currentUser?._emailVerified) return;
        const filter = {
            currentlyAssignedTo: currentUser._id,
            currentStatus: ETaskStatus.COMPLETED,
        }
        const tasks = await fetchTasksByFilter(filter,
            tasksAsFreelancerCompletedPage,
            tasksAsFreelancerCompletedLimit);
        setTasksAsFreelancerCompleted(tasks);
    }

    const fetchAvailableTasksAsFreelancer = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER) return;

        const tasks = await fetchAllAvailableTasksAsFreelancer(
            availableTasksAsFreelancerPage,
            availableTasksAsFreelancerLimit);
        setAvailableTasksAsFreelancer(tasks || []);
    }

    const updateTaskStatusToDeliveryProvided = async (taskId: string): Promise<ITask | undefined> => {
        try {
            const task = await _updateStatus(taskId, ETaskStatus.DELIVERY_PROVIDED);
            const newTasksAsFreelancerInProgress = tasksAsFreelancerInProgress.map((t) => {
                if (t._id === taskId) {
                    return task;
                }
                return t;
            });
            setTasksAsFreelancerInProgress(newTasksAsFreelancerInProgress);

            setProjectTasks((prevProjectTasks) => ({
                ...prevProjectTasks,
                [task.projectId!]: prevProjectTasks[task.projectId!]?.map((t) =>
                    t._id === taskId ? task : t
                ),
            }));

            setTasksAsFreelancerInProgress([...tasksAsFreelancerInProgress.map(t => task._id === t._id ? task : t)]);
            return task;
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            })
        }

    }

    return (
        <MyTasksContext.Provider value={{
            applications,
            invitations,

            pendingApplicationsTasks,
            acceptedApplicationsTasks,
            rejectedApplicationsTasks,
            pendingInvitationsTasks,

            tasksAsFreelancerInProgress,
            tasksAsFreelancerCompleted,
            availableTasksAsFreelancer,

            projectTasks,
            fetchPendingApplications,
            fetchAcceptedApplications,
            fetchRejectedApplications,
            fetchPendingInvitations,

            replaceProjectTaskLocally,
            fetchProjectTasks,
            createProjectTask,
            generateProjectTasksAi,
            updateProjectTask,
            pushRankProjectTask,
            startAndPayTask,
            deleteProjectTask,
            addFreelancerHours,

            applyForTask,
            confirmApplication,
            refuseApplication,

            acceptInvitation,
            declineInvitation,

            fetchAvailableTasksAsFreelancer,
            fetchTasksAsFreelancerInProgress,
            fetchTasksAsFreelancerCompleted,

            updateTaskStatusToDeliveryProvided,
        }}>
            {children}
        </MyTasksContext.Provider>
    );
};
